/**
 * "Follow one person" — one example customer, step by step, through all four
 * parts. The concept's story (`wend-concept/.../journey.jsx`), retold so that
 * every step is something Wend does today.
 *
 * ## IT IS MADE UP
 *
 * Acme Retail, Priya Shinde, Ananya and every figure here are made up (the
 * header's "An example" label was removed at the owner's request, Oct 2026),
 * and the phone number is drawn
 * already masked (`+91 98••• ••417`) — the product masks numbers the same way
 * on every screen that lists people, so the example shows the real habit and
 * contains no number at all.
 *
 * ## WHERE THE STORY DEPARTS FROM THE CONCEPT, AND WHY
 *
 *   - **Step 1 asks no questions.** The concept's lead form collected a city
 *     and a birthday. Wend's built lead path is click-to-WhatsApp: the lead
 *     carries a number and the ad (`ad_id`, headline, link, time, channel).
 *     The data-that-moved rows are exactly those keys.
 *   - **Step 3's rules target the ad, not an age.** Priya has no birthday on
 *     file, and a rule cannot match a field the person does not have. Derived
 *     Age is shown working in the segment builder demo further down instead.
 *   - **The offer waits for the frequency cap (step 7).** The concept proposed
 *     exempting a message wired to a tapped button. That exemption is not
 *     built: the engine counts every flow and campaign message in the window
 *     and defers what would go over. So the story shows the deferral, which is
 *     the more useful thing for a buyer to see anyway.
 *   - **The goal ends the journey; nothing is sent to Meta (step 8).** The
 *     concept added Priya to an ad exclusion list "if approved". Not built.
 *   - **The order is uploaded.** Outcomes reach Wend as a recorded outcome (an
 *     upload or a declared one), not through a shop integration, so the story
 *     says Acme uploads the day's orders.
 *   - **No lift figure in step 9.** Eleven held-out people is below the floor
 *     under which Sense shows no rate at all — a missing figure, never a zero
 *     and never a guess. The concept drew a confidence interval; Wend's rule is
 *     simpler and stricter.
 *
 * Each step's `edge` names the edge in the loop above it that the step moves
 * data along (an id from `sections/Loop.tsx`'s edge list), so the picture
 * lights up as the reader steps.
 */
import type { ReactNode } from 'react';

import type { PartId } from './parts';

export interface ThreadMessage {
  readonly who: 'her' | 'brand';
  readonly text: string;
  readonly time: string;
  readonly buttons?: readonly string[];
  readonly tapped?: string;
}

export interface JourneyFinding {
  readonly kicker: string;
  readonly title: string;
  readonly detail: string;
  /** Drawn quieter: a reading that is not ready yet. */
  readonly early?: boolean;
}

export interface JourneyStep {
  readonly part: PartId;
  readonly edge: string;
  readonly when: string;
  readonly title: string;
  readonly body: ReactNode;
  readonly note?: string;
  readonly rules?: readonly string[];
  readonly finding?: JourneyFinding;
  readonly thread?: readonly ThreadMessage[];
  readonly moved: {
    readonly from: string;
    readonly to: string;
    readonly what: string;
    readonly rows: readonly (readonly [string, string])[];
  };
}

export const JOURNEY_PERSON = {
  initials: 'PS',
  name: 'Priya Shinde',
  meta: '29 · Pune · a new lead from the Diwali ad',
  business: 'Acme Retail',
} as const;

const PHONE = '+91 98••• ••417';

const FIRST_MESSAGE: ThreadMessage = { who: 'her', text: 'Hi! Is the chikankari kurta set available in M?', time: '20:14' };

export const JOURNEY: readonly JourneyStep[] = [
  {
    part: 'reach',
    edge: 'reach>people',
    when: 'Sun 4 Oct · 20:14',
    title: 'She taps an ad and writes in',
    body: (
      <>
        A click-to-WhatsApp ad on Acme’s own Meta ad account, published paused and started by Ananya.
      </>
    ),
    thread: [FIRST_MESSAGE],
    moved: {
      from: 'Ad tap',
      to: 'Reach leads',
      what: 'new lead',
      rows: [
        ['phone', PHONE],
        ['ad_id', '23851240117'],
        ['ad_headline', 'Diwali offer · Pune'],
      ],
    },
  },
  {
    part: 'people',
    edge: 'people>audiences',
    when: '+2s',
    title: 'She becomes one person',
    body: <>New number, new person. The ad she came from is a field like any other.</>,
    moved: {
      from: 'Reach leads',
      to: 'People',
      what: 'person created',
      rows: [
        ['Source', 'Reach lead'],
        ['Ad', 'Diwali offer · Pune'],
      ],
    },
  },
  {
    part: 'audiences',
    edge: 'audiences>connect',
    when: '+6s',
    title: 'She enters a rule segment',
    body: <>The segment refreshes as the lead lands and records why she entered.</>,
    rules: ['Source is Reach lead', 'Ad is Diwali offer · Pune', 'First seen in the last 14 days'],
    moved: {
      from: 'Audiences',
      to: 'Connect · Flow',
      what: 'entered',
      rows: [
        ['segment', 'New leads · Diwali ads'],
        ['cause', 'a new lead'],
      ],
    },
  },
  {
    part: 'connect',
    edge: 'connect>sense',
    when: 'Mon 5 Oct · 09:30',
    title: 'A welcome from Acme’s own number',
    body: (
      <>
        Ananya sends the <code>diwali_welcome</code> template. Priya reads it and does not reply.
      </>
    ),
    thread: [
      { ...FIRST_MESSAGE, time: 'Sun 20:14' },
      { who: 'brand', text: 'Hi Priya! Yes, it’s in stock in M. Diwali prices hold till 20 Oct.', buttons: ['Show me sizes', 'Not now'], time: '09:30' },
    ],
    moved: {
      from: 'Connect',
      to: 'Sense',
      what: 'per-person delivery',
      rows: [
        ['delivered', '09:30:05'],
        ['read', '10:11'],
        ['replied', 'no'],
      ],
    },
  },
  {
    part: 'sense',
    edge: 'sense>flow',
    when: 'Thu 8 Oct · 09:00',
    title: 'Sense notices the silence',
    body: <>It offers to draft a reminder flow. It drafts; it does not send.</>,
    finding: {
      kicker: 'Sense · Today',
      title: 'New leads not replying · 115 people',
      detail: 'no reply in 3 days · nothing scheduled · Draft a flow',
    },
    moved: {
      from: 'Sense',
      to: 'Flow',
      what: 'draft flow',
      rows: [
        ['people', '115'],
        ['launched by Sense', 'never'],
      ],
    },
  },
  {
    part: 'flow',
    edge: 'sense>flow',
    when: 'Fri 9 Oct · 18:02',
    title: 'Ananya edits, tests, launches',
    body: <>Test walks Priya through it and sends nothing. Launched with a 5% holdout.</>,
    moved: {
      from: 'Sense draft',
      to: 'Flow · live',
      what: 'launched',
      rows: [
        ['holdout', '5%'],
        ['frequency cap', '2 messages / 7 days'],
        ['quiet hours', '21:00–09:00 IST'],
      ],
    },
  },
  {
    part: 'flow',
    edge: 'flow>connect',
    when: 'Sat 10 Oct · 10:00',
    title: 'She taps “Show me”, and the offer waits',
    body: <>Each button is its own path. The cap is full, so the offer waits until Monday 09:31.</>,
    thread: [
      { who: 'brand', text: 'Still thinking about the kurta set, Priya?', buttons: ['Show me', 'Not now'], tapped: 'Show me', time: '10:00' },
    ],
    moved: {
      from: 'Flow',
      to: 'Connect',
      what: 'per-step',
      rows: [
        ['button', '“Show me”'],
        ['offer', 'held by the frequency cap'],
        ['due', 'Mon 12 Oct 09:31'],
      ],
    },
  },
  {
    part: 'flow',
    edge: 'flow>sense',
    when: 'Mon 12 Oct · 18:00',
    title: 'She orders. The journey ends.',
    body: <>Acme uploads the day’s orders; hers matches by number. Goal reached: no more messages.</>,
    thread: [{ who: 'brand', text: 'Here’s 10% off for Diwali: DIWALI10.', time: '09:31' }],
    moved: {
      from: 'Order upload',
      to: 'Flow',
      what: 'goal reached',
      rows: [
        ['order', '#AC-20417 · ₹2,340'],
        ['journey', 'converted · ends'],
        ['sent to Meta', 'nothing'],
      ],
    },
  },
  {
    part: 'sense',
    edge: 'flow>sense',
    when: 'Sat 17 Oct',
    title: 'Sense reads it against the holdout',
    body: <>Eleven held-out people is too few to state a lift, so Sense shows none yet: not a zero, not a guess.</>,
    finding: {
      kicker: 'Sense · Too early to call',
      title: 'Lift not readable yet',
      detail: 'treated 27 of 203 · held out 1 of 11',
      early: true,
    },
    moved: {
      from: 'Flow',
      to: 'Sense',
      what: 'holdout reading',
      rows: [
        ['lift', 'not shown · below the floor'],
      ],
    },
  },
];

/** The step a key moves to, or null when the key is not one the stepper handles. Pure, so the suite can test it. */
export function stepForKey(step: number, key: string, total: number = JOURNEY.length): number | null {
  const clamp = (value: number): number => Math.max(0, Math.min(total - 1, value));
  if (key === 'ArrowRight') return clamp(step + 1);
  if (key === 'ArrowLeft') return clamp(step - 1);
  if (key === 'Home') return 0;
  if (key === 'End') return total - 1;
  return null;
}

/** The first step of the story a part appears in, 0-based, or null. */
export function firstStepOf(part: PartId): number | null {
  const index = JOURNEY.findIndex((step) => step.part === part);
  return index === -1 ? null : index;
}
